// src/theme/createAppTheme.ts
//
// Single source of truth for the application theme. Builds a full MUI theme for
// a given mode ('light' | 'dark') sharing typography, the `custom` design tokens
// and component overrides; only the palette differs. The MUI module augmentation
// (custom / blue / white / TypeBackground / button + icon variants) lives in
// src/types/theme.d.ts and is preserved untouched.
import { defaultTheme, defaultDarkTheme } from 'react-admin';
import { alpha, createTheme, Palette, Theme } from '@mui/material/styles';
import { lightPalette, darkPalette, brandTextTeal } from './palette';

type ThemeMode = 'light' | 'dark';

const radius = {
    small: '6px',    // Buttons, inputs, menu items
    medium: '7px',   // Compact toolbar controls (search boxes, toolbar inputs)
    large: '10px',   // Large containers
    card: '10px',    // Cards, tables, popovers — dedicated token, not "medium"
    chip: '9999px',  // Chips/badges render as fully-rounded pills
    full: '9999px',  // Circular elements
};

// 3c Hybrid outline system (light mode; hybrid-3c.md §2 "Lines, radii,
// shadows"). Definition comes from ONE thin light line, not a dark one:
// `borderCard` = `borderRow` = `palette.divider` = #E2DFD9, for card frames,
// table frames and row separators alike. Chips carry no dark line either:
// their border is one tone darker than their own wash (see MuiChip). Dark
// mode keeps its slate divider (light-only migration).
const borderCard = '#E2DFD9';
const borderRow = '#E2DFD9';
const cardShadow = '0 6px 16px -12px rgba(63,49,25,0.35)';

// Solid-button fill (hybrid-3c.md §2) — a mid teal-ink, distinct from Signal
// Teal (primary.main #0BA5B7) so filled CTAs don't clash with the teal chrome
// (rail / accents). primary.main still drives links, focus, the active menu
// item and small accents; amber/ochre stays reserved for status.
const CTA_FILL = '#117E89';
const CTA_FILL_HOVER = '#0F6C76';

// Shared design tokens — "3c hybrid" direction (engineering-tool density:
// hairline borders instead of card shadows, dense tables, solid Deep Teal
// rail). Function members receive the palette/theme at call time, so they
// stay mode-aware automatically.
const buildCustom = (mode: ThemeMode) => {
    const isLight = mode !== 'dark';
    // Muted label colour shared by section eyebrows and table headers
    // (hybrid-3c.md §2 "Ink": custom.text.label).
    const headerLabelColor = isLight ? '#837868' : '#94A3B8';
    return {
        radius,
        // Shadows are reserved for surfaces that truly float above content
        // (popovers, menus, dialogs); cards and tables use a 1px hairline
        // border. `subtle`/`medium` sit on a WARM base (hybrid-3c.md §2),
        // not the previous cool rgba(20,40,60,…) one.
        shadows: {
            none: '0 1px 2px 0 rgb(0 0 0 / 0.01)',
            subtle: isLight
                ? '0 1px 2px rgba(80,60,25,.04), 0 4px 16px rgba(80,60,25,.05)'
                : '0 1px 2px rgba(20,40,60,0.04), 0 4px 16px rgba(20,40,60,0.05)',
            medium: isLight
                ? '0 4px 12px rgba(80,60,25,.10), 0 12px 32px rgba(80,60,25,.08)'
                : '0 4px 12px rgba(20,40,60,0.10), 0 12px 32px rgba(20,40,60,0.08)',
            strong: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
            login: '0 25px 50px -12px rgb(0 0 0 / 0.15)',
            loginButton: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        },
        // Soft halo shown on :focus-visible across interactive chrome.
        focusRing: '0 0 0 3px rgba(11, 165, 183, 0.25)',
        // Brand-tinted "opportunity" role (something the person could add or try, e.g.
        // the AI-assistant entry): a 10% deep-teal wash with deep-teal ink (8.47:1 in
        // light). Same recipe as roommatik.console.ui `custom.status.opportunity`.
        status: {
            opportunity: isLight
                ? { bg: alpha('#0E7C8A', 0.1), text: '#084C54' }
                : { bg: alpha(brandTextTeal.dark, 0.16), text: brandTextTeal.dark },
        },
        // v3 solid-button fill (teal-ink) — shared by MuiButton (contained) and
        // the user avatar. Kept mode-agnostic; dark chrome consumes primary.main.
        ctaFill: CTA_FILL,
        ctaFillHover: CTA_FILL_HOVER,
        // Micro-tones not modeled by MUI's two-tier text.primary/secondary
        // (page subtitles, table header labels, monospace IDs).
        text: {
            tertiary: isLight ? '#746E63' : '#94A3B8',
            label: headerLabelColor,
            idMono: isLight ? '#A19A8D' : '#64748B',
            // v2 teal-as-text: eyebrow labels and inline links/actions.
            brand: isLight ? brandTextTeal.light : brandTextTeal.dark,
            // v3.11: stronger teal-as-text for headings sitting on tinted/warm
            // bands (not white) — e.g. the activity-monitor column title on the
            // grey.200 header band, where `brand` (#147A87) drops below AA. This
            // deep teal keeps the teal-as-text identity while passing AA (~5.7:1).
            brandStrong: isLight ? '#0C5A66' : brandTextTeal.dark,
        },
        // Dense table surfaces — hairline borders instead of shadows.
        // hybrid-3c.md §9: one light hairline (borderCard/borderRow, both
        // #E2DFD9) for the frame, the header line AND the row separator.
        // Dark keeps its slate values.
        table: {
            headerBg: isLight ? '#FAF8F3' : '#1B2434',
            headerBorder: isLight ? borderCard : 'rgba(148, 163, 184, 0.34)',
            rowBorder: isLight ? borderRow : 'rgba(148, 163, 184, 0.20)',
            // Row hover — a named token (hybrid-3c.md §9 "Rows: hover
            // #F1EDE6") so RaDatagrid and every shell datagrid share one
            // value instead of each picking a nearby grey.
            rowHover: isLight ? '#F1EDE6' : 'rgba(148, 163, 184, 0.08)',
            // Single source of truth for table-header label typography. v3: ink
            // (not teal), weight 600, sentence case, 14px. Every datagrid and
            // hand-built table consumes this — never restyle header labels locally.
            headCell: {
                fontSize: '0.875rem',
                fontWeight: 600,
                textTransform: 'none' as const,
                letterSpacing: '0.01em',
                lineHeight: 1.3,
                color: isLight ? '#282624' : brandTextTeal.dark,
            },
        },
        login: {
            containerGradient: (palette: Palette) => `linear-gradient(135deg, ${palette.grey[50]} 0%, ${palette.grey[200]} 100%)`,
            topBarGradient: (palette: Palette) => `linear-gradient(90deg, ${palette.primary.main} 0%, ${palette.primary.light} 100%)`,
            fieldBackground: (palette: Palette) => palette.grey[50],
            logoFilter: 'drop-shadow(0 4px 6px rgb(0 0 0 / 0.1))',
        },
        layout: {
            desktopContainerPadding: '8px 8px 8px 8px',
            gap: '0px',
            mobileContainerPadding: '4px',
            mobileTopMargin: '16px',
            card: {
                borderRadius: radius.card,
                boxShadow: 'none',
                padding: { xs: '16px', sm: '24px' },
            },
        },
        userMenu: {
            paper: {
                width: '280px',
                borderRadius: radius.large,
                boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
            },
        },
        // v3.8bis: user avatar is a teal-ink disc with white initials.
        userAvatar: {
            size: '40px',
            bg: isLight ? CTA_FILL : '#334155',
            color: isLight ? '#FFFFFF' : '#F1F5F9',
            iconSize: '24px',
        },
        // 3c Hybrid rail (hybrid-3c.md §4): a light neutral surface a shade
        // off the canvas — NOT a brand-colour block. What separates it from
        // the canvas is its right-hand border, not a fill; teal is spent on
        // the active entry alone (rule 1). Rail background lives in
        // palette.background.menu (single source of truth).
        menu: {
            openWidth: isLight ? '240px' : '230px',
            closedWidth: '64px',
            // Right-hand hairline (light) — same tone as card/table borders.
            // Dark mode keeps its previous darkening line against the solid fill.
            border: isLight ? '#E2DFD9' : 'rgba(0,0,0,0.08)',
            // Insight keeps uppercase group labels (Recepción/Dirección/…);
            // the spec's alternative is a bare hairline separator between
            // groups (`separatorColor`, below) — offered here too so a group
            // boundary can be rendered either way without a second token set.
            groupLabelColor: isLight ? '#837868' : 'rgba(255,255,255,0.55)',
            separatorColor: isLight ? 'rgba(40,38,36,0.10)' : 'rgba(255,255,255,0.10)',
            item: {
                borderRadius: radius.small,
                margin: '2px 0',
                padding: isLight ? '8px 8px' : '7px 8px',
                gap: '12px',
                // Row typography (hybrid-3c.md §4 "Entry row height 36px
                // fixed — 14px label… weight 500"); dark mode keeps its
                // previous metrics (hardcoded in CustomMenu before this
                // token existed) so it still compiles and renders unchanged.
                fontSize: isLight ? '14px' : '13.5px',
                lineHeight: isLight ? '20px' : '19px',
                minHeight: isLight ? '36px' : '36px',
                iconSize: isLight ? '18px' : '20px',
                color: isLight ? () => '#3C3934' : () => 'rgba(255,255,255,0.85)',
                iconColor: isLight ? () => '#77726A' : () => 'rgba(255,255,255,0.7)',
                hover: isLight
                    ? {
                          color: () => '#282624',
                          backgroundColor: () => 'rgba(40,38,36,0.05)',
                      }
                    : {
                          color: () => '#FFFFFF',
                          backgroundColor: () => 'rgba(255,255,255,0.08)',
                      },
                // Active entry: a teal WASH derived from primary.dark via
                // alpha() (never a frozen hex), a 3px leading marker, no
                // border and no shadow (hybrid-3c.md §4 "Entry states").
                active: isLight
                    ? {
                          color: () => '#084C54',
                          backgroundColor: (theme: Theme) => alpha(theme.palette.primary.dark, 0.1),
                          fontWeight: 600,
                          marker: (theme: Theme) => theme.palette.primary.dark,
                          markerWidth: '3px',
                          markerInset: '8px',
                          markerRadius: '0 3px 3px 0',
                          hover: {
                              backgroundColor: (theme: Theme) => alpha(theme.palette.primary.dark, 0.15),
                          },
                      }
                    : {
                          color: () => '#FFFFFF',
                          backgroundColor: () => 'rgba(255,255,255,0.16)',
                          fontWeight: 600,
                          marker: () => '#FFFFFF',
                          markerWidth: '2px',
                          markerInset: '6px',
                          markerRadius: '0 2px 2px 0',
                          hover: {
                              backgroundColor: () => 'rgba(255,255,255,0.22)',
                          },
                      },
                closedActiveIcon: isLight
                    ? {
                          backgroundColor: (theme: Theme) => alpha(theme.palette.primary.dark, 0.1),
                          color: () => '#084C54',
                          borderRadius: '6px',
                      }
                    : {
                          backgroundColor: () => 'rgba(255,255,255,0.16)',
                          color: () => '#FFFFFF',
                          borderRadius: '6px',
                      },
            },
            // Two-tone brand lockup at the rail top (hybrid-3c.md §4 "Brand
            // lockup"): ink now that the surface is light. Dark mode keeps
            // the previous white-on-teal reading.
            brand: {
                color: isLight ? '#0E7C8A' : '#FFFFFF',
                mutedColor: isLight ? '#77726A' : 'rgba(255,255,255,0.72)',
            },
            // Recessed profile plate at the rail foot (hybrid-3c.md §4
            // "Profile block"). New token group — the component previously
            // hardcoded a translucent-white card inline.
            profile: {
                bg: isLight ? '#EDEAE4' : 'rgba(255,255,255,0.08)',
                border: isLight ? 'rgba(40,38,36,0.08)' : 'rgba(255,255,255,0.18)',
                hoverBg: isLight ? '#E7E3DC' : 'rgba(255,255,255,0.14)',
                borderRadius: radius.large,
                nameColor: isLight ? '#282624' : '#FFFFFF',
                roleColor: isLight ? '#746E63' : 'rgba(255,255,255,0.7)',
                chevronColor: isLight ? '#77726A' : 'rgba(255,255,255,0.7)',
                avatarBg: isLight ? '#0A5F69' : '#FFFFFF',
                avatarColor: isLight ? '#FFFFFF' : (theme: Theme) => theme.palette.primary.dark,
                avatarSize: 32,
                nameFontSize: '13px',
                nameFontWeight: 600,
                roleFontSize: '12px',
                roleFontWeight: 400,
            },
            // Thin, idle-hidden rail scrollbar (hybrid-3c.md §4 "Rail
            // scrollbar") — the ink scale reads correctly on a light rail;
            // the global cream scrollbar (index.css) is tuned for the canvas.
            scrollbarThumb: isLight ? 'rgba(40,38,36,0.18)' : 'rgba(255,255,255,0.3)',
            scrollbarThumbHover: isLight ? 'rgba(40,38,36,0.30)' : 'rgba(255,255,255,0.45)',
        },
    };
};

const typography = {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 14,
    htmlFontSize: 16,
    // v3 typography — rebalanced: more size, less weight. Weight 600 lives ONLY
    // in h1–h3, the datagrid header and active menu items; everything else is
    // lighter. Page title (h1) is the section-header size.
    h1: { fontSize: '1.625rem', fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.02em' },
    h2: { fontSize: '1.375rem', fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.015em' },
    h3: { fontSize: '1.1875rem', fontWeight: 600, lineHeight: 1.35, letterSpacing: '-0.01em' },
    h4: { fontSize: '1.0625rem', fontWeight: 500, lineHeight: 1.4 },
    h5: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.45 },
    h6: { fontSize: '0.9375rem', fontWeight: 500, lineHeight: 1.45, letterSpacing: '0.01em' },
    body1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.55 },
    caption: { fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.5 },
    // v2 eyebrow: short uppercase teal guide-label above important blocks/cards
    // ("OPERACIÓN", "CHECK-INS DE HOY"). Colour is applied mode-aware in
    // createAppTheme (custom.text.brand).
    overline: {
        fontSize: '0.6875rem',
        fontWeight: 800,
        letterSpacing: '0.08em',
        lineHeight: 1.5,
        textTransform: 'uppercase' as const,
    },
    button: { fontWeight: 500, letterSpacing: '0.02em' },
};

export const createAppTheme = (mode: ThemeMode): Theme => {
    const raBase = mode === 'dark' ? defaultDarkTheme : defaultTheme;
    const palette = mode === 'dark' ? darkPalette : lightPalette;
    const isLight = mode !== 'dark';
    const custom = buildCustom(mode);

    // Base theme first, so component overrides can read resolved palette values.
    const baseTheme = createTheme({
        ...raBase,
        palette,
        custom,
        // Global default radius for components that don't reference custom.radius
        // (outlined inputs, selects, menus, popovers) — matches the compact control radius.
        shape: { borderRadius: 7 },
        sidebar: {
            width: isLight ? 240 : 230,      // must match custom.menu.openWidth
            closedWidth: 64,  // must match custom.menu.closedWidth
        },
        typography: {
            ...typography,
            // Muted caption tone, mode-aware (was a fixed light slate-500).
            caption: { ...typography.caption, color: mode === 'dark' ? '#94A3B8' : '#64748B' },
            // Eyebrow colour: teal-as-text, mode-aware.
            // Eyebrow labels are neutral, not teal (hybrid-3c.md rule 1
            // "teal marks one thing at a time" — the active rail entry,
            // links, focus; never a section label repeated on every card).
            // `custom.text.brand` stays teal for MuiLink below, which rule 1
            // explicitly allows.
            overline: { ...typography.overline, color: custom.text.label },
        },
    });

    // Colored-chip/text helper: dark mode needs the lighter shade for legibility
    // on dark surfaces, light mode the darker shade on light surfaces.
    const onSurface = (c: Palette['primary']) =>
        baseTheme.palette.mode === 'dark' ? c.light : c.dark;

    return createTheme({
        ...baseTheme,
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    '.RaList-main .RaList-actions': {
                        borderBottom: 'none !important',
                        alignItems: 'center !important',
                        paddingBottom: '4px',
                    },
                    '.RaList-main .RaList-actions form > .MuiBox-root': {
                        alignItems: 'center !important',
                        marginBottom: '0 !important',
                    },
                    // Visible focus halo on keyboard navigation (AA): replace the
                    // default outline with a brand-tinted ring that works against
                    // both paper and chrome surfaces.
                    '*:focus-visible': {
                        outline: 'none',
                        boxShadow: custom.focusRing,
                        borderRadius: radius.small,
                    },
                    'button:focus-visible, a:focus-visible, input:focus-visible, select:focus-visible, textarea:focus-visible': {
                        outline: 'none',
                        boxShadow: custom.focusRing,
                    },
                },
            },

            MuiButton: {
                defaultProps: {
                    size: 'small' as const,
                    // Flat, Stripe-like contained buttons (no elevation shadow).
                    disableElevation: true,
                },
                styleOverrides: {
                    root: {
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontWeight: 500,
                        // Slightly rounded (7px), not pill — flat, compact (~38px tall).
                        borderRadius: radius.medium,
                        textTransform: 'none',
                        minHeight: '38px',
                        padding: '8px 14px',
                        boxShadow: 'none',
                        transition: 'background-color 0.3s ease, color 0.3s ease',
                        '&:hover': { boxShadow: 'none' },
                    },
                    // v3 solid CTA — dark teal-ink fill (light), so it doesn't clash
                    // with the teal chrome. Dark keeps its primary fill.
                    contained: {
                        backgroundColor: isLight ? CTA_FILL : baseTheme.palette.primary.main,
                        color: isLight ? '#FFFFFF' : baseTheme.palette.primary.contrastText,
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: isLight ? CTA_FILL_HOVER : baseTheme.palette.primary.dark,
                            boxShadow: 'none',
                        },
                    },
                    // v3 secondary button — white fill + neutral border + dark text
                    // (Refresh / Export / Filters, tabs). Not MUI's transparent outlined.
                    outlined: {
                        backgroundColor: isLight ? '#FFFFFF' : baseTheme.palette.action.hover,
                        borderColor: baseTheme.palette.divider,
                        color: baseTheme.palette.text.primary,
                        '&:hover': {
                            backgroundColor: baseTheme.palette.action.hover,
                            borderColor: baseTheme.palette.divider,
                        },
                    },
                },
                // `loginButton`/`createButton`/`actionButton` (v3 pill-toolbar
                // variants) were removed here: rule 4 "one button system" and a
                // repo-wide grep confirmed no screen still passes them — the
                // outlined/contained recipe above already covers what they did.
            },

            // v2 inline links/actions: teal-as-text, weight 700 (web look).
            MuiLink: {
                styleOverrides: {
                    root: {
                        color: custom.text.brand,
                        fontWeight: 700,
                        textDecorationColor: alpha(custom.text.brand, 0.4),
                        '&:hover': {
                            textDecorationColor: custom.text.brand,
                        },
                    },
                },
            },

            // Icon buttons are ghosts (hybrid-3c.md §7 "Icon buttons are
            // ghosts"): no border at rest, the icon itself is the
            // affordance, and hover is a quiet neutral ink wash — not a
            // solid teal fill. The colourful variants keep their own tint
            // (rule kept: no border, `${color}14` tinted hover).
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        border: 'none',
                        borderRadius: radius.small,
                        color: baseTheme.palette.text.secondary,
                        width: 36,
                        height: 32,
                        '&:hover': {
                            backgroundColor: isLight ? 'rgba(40,38,36,0.05)' : baseTheme.palette.action.hover,
                            color: baseTheme.palette.text.primary,
                        },
                    },
                    colorInherit: {
                        color: baseTheme.palette.text.secondary,
                        '&:hover': {
                            color: baseTheme.palette.primary.main,
                            backgroundColor: baseTheme.palette.action.hover,
                        },
                    },
                    colorPrimary: {
                        color: baseTheme.palette.primary.main,
                        '&:hover': {
                            backgroundColor: `${baseTheme.palette.primary.main}14`,
                        },
                    },
                    colorSecondary: {
                        color: baseTheme.palette.secondary.main,
                        '&:hover': {
                            backgroundColor: `${baseTheme.palette.secondary.main}14`,
                        },
                    },
                    colorError: {
                        color: baseTheme.palette.error.main,
                        '&:hover': {
                            backgroundColor: `${baseTheme.palette.error.main}14`,
                        },
                    },
                    colorWarning: {
                        color: baseTheme.palette.warning.main,
                        '&:hover': {
                            backgroundColor: `${baseTheme.palette.warning.main}14`,
                        },
                    },
                    colorInfo: {
                        color: baseTheme.palette.info.main,
                        '&:hover': {
                            backgroundColor: `${baseTheme.palette.info.main}14`,
                        },
                    },
                    colorSuccess: {
                        color: baseTheme.palette.success.main,
                        '&:hover': {
                            backgroundColor: `${baseTheme.palette.success.main}14`,
                        },
                    },
                    sizeLarge: {
                        width: 48,
                        height: 48,
                    },
                    sizeSmall: {
                        width: 32,
                        height: 32,
                    },
                },
            },

            MuiBadge: {
                defaultProps: {
                    color: 'primary',
                    max: 99,
                    anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
                },
                styleOverrides: {
                    badge: ({ theme }) => ({
                        borderRadius: theme.custom?.radius?.chip ?? '4px',
                        minWidth: 18,
                        height: 18,
                        padding: '0 4px',
                        border: `2px solid ${theme.palette.background.paper}`,
                        backgroundColor: theme.palette.primary.dark,
                        color: theme.palette.common.white,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 600,
                        lineHeight: 1,
                    }),
                },
            },

            MuiDialogActions: {
                styleOverrides: {
                    root: {
                        padding: '16px',
                        fontWeight: 500,
                        minWidth: 'auto',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        '& .MuiButton-text': {
                            backgroundColor: baseTheme.palette.action.hover,
                            color: baseTheme.palette.text.secondary,
                            border: `1px solid ${baseTheme.palette.divider}`,
                            '&:hover': {
                                backgroundColor: baseTheme.palette.action.selected,
                            },
                        },
                        '& .MuiButton-contained': {
                            backgroundColor: isLight ? CTA_FILL : baseTheme.palette.primary.main,
                            color: isLight ? '#FFFFFF' : baseTheme.palette.primary.contrastText,
                            border: `1px solid ${isLight ? CTA_FILL : baseTheme.palette.primary.main}`,
                            '&:hover': {
                                backgroundColor: isLight ? CTA_FILL_HOVER : baseTheme.palette.primary.dark,
                                borderColor: isLight ? CTA_FILL_HOVER : baseTheme.palette.primary.dark,
                            },
                        },
                        '& .MuiSvgIcon-root': {
                            fontSize: '1.25rem',
                        },
                    },
                },
            },

            RaList: {
                styleOverrides: {
                    root: {
                        '& .RaList-main': {
                            paddingTop: 12,
                            paddingLeft: 16,
                            paddingRight: 16,
                        },
                    },
                },
            },

            RaListToolbar: {
                styleOverrides: {
                    root: {
                        alignItems: 'center',
                        gap: '8px',
                    },
                },
            },

            RaFilterForm: {
                styleOverrides: {
                    root: {
                        minHeight: 'auto !important',
                        alignItems: 'center',
                        padding: 0,
                        gap: baseTheme.spacing(1),
                        '& .RaFilterForm-filterFormInput .MuiFormControl-root': {
                            marginTop: 0,
                        },
                    },
                },
            },

            RaTopToolbar: {
                styleOverrides: {
                    root: {
                        backgroundColor: 'transparent',
                        marginBottom: 0,
                    },
                },
            },

            RaDatagrid: {
                styleOverrides: {
                    root: {
                        backgroundColor: baseTheme.palette.background.paper,
                        // One hairline tone for the whole frame (hybrid-3c.md
                        // §9 "Datagrid"): no shadow, borderCard everywhere.
                        border: `1px solid ${custom.table.headerBorder}`,
                        boxShadow: 'none',
                        borderRadius: radius.card,
                        overflow: 'hidden',

                        // Header label typography comes from the single source of
                        // truth (custom.table.headCell); only structural styling
                        // (background, border, padding) lives here.
                        '& .RaDatagrid-headerCell': {
                            backgroundColor: custom.table.headerBg,
                            borderBottom: `1px solid ${custom.table.headerBorder}`,
                            padding: '10px 14px',
                            ...custom.table.headCell,
                        },

                        '& .RaDatagrid-row': {
                            backgroundColor: baseTheme.palette.background.paper,
                            transition: 'background-color 0.12s ease',
                            '&:hover': {
                                backgroundColor: isLight ? custom.table.rowHover : baseTheme.palette.action.hover,
                            },
                        },

                        '& .RaDatagrid-rowCell': {
                            fontSize: '0.8125rem',
                            padding: '8px 14px',
                            borderBottom: `1px solid ${custom.table.rowBorder}`,
                            color: baseTheme.palette.text.primary,
                            '&:last-of-type': {
                                borderRight: 'none',
                            },
                        },

                        'tbody tr:last-child .RaDatagrid-rowCell': {
                            borderBottom: 'none',
                        },

                        '& .MuiTablePagination-root': {
                            borderTop: `1px solid ${baseTheme.palette.divider}`,
                            backgroundColor: baseTheme.palette.background.default,
                            padding: baseTheme.spacing(1),
                        },

                        '& .RaDatagrid-rowCell:has(button)': {
                            paddingTop: '6px',
                            paddingBottom: '6px',
                            textAlign: 'right',
                            whiteSpace: 'nowrap',
                        },

                        '& button.RaButton-link': {
                            fontSize: '0.8rem',
                            color: baseTheme.palette.text.secondary,
                            padding: '4px 8px',
                            borderRadius: radius.small,
                            transition: 'color 0.2s ease, background-color 0.2s ease',
                            '&:hover': {
                                color: baseTheme.palette.primary.main,
                                backgroundColor: `${baseTheme.palette.primary.light}10`,
                            },
                        },

                        '& button[aria-label="Delete"]': {
                            color: baseTheme.palette.primary.main,
                            width: '36',
                            '&:hover': {
                                color: baseTheme.palette.common.white,
                                backgroundColor: baseTheme.palette.error.main,
                            },
                        },
                    },
                },
            },

            // v3.12: a light sand 1px border + a soft warm shadow (light mode) —
            // cards lift gently off the warm canvas. Dark mode keeps the flat,
            // borderless-on-divider look (no warm shadow on dark surfaces).
            MuiCard: {
                defaultProps: {
                    elevation: 0,
                },
                styleOverrides: {
                    root: {
                        backgroundColor: baseTheme.palette.background.paper,
                        borderRadius: radius.card,
                        boxShadow: isLight ? cardShadow : 'none',
                        border: `1px solid ${isLight ? borderCard : baseTheme.palette.divider}`,
                        marginBottom: baseTheme.spacing(1),
                    },
                },
            },

            MuiTableCell: {
                styleOverrides: {
                    root: {
                        padding: '8px 14px',
                    },
                },
            },

            // Chip system: a light wash of the chip's hue with a thin border ONE tone
            // darker than that same wash (never the dark text colour, never a
            // heavy line). Neutral = warm grey wash + the next warm grey as border;
            // coloured states = `${main}14` wash + `${main}40` border, dark text of
            // the hue for contrast. Borderless (`solid`) is the one-per-view accent.
            // Dark keeps the soft tint without a border.
            MuiChip: {
                styleOverrides: {
                    root: {
                        borderRadius: radius.chip,
                        fontWeight: isLight ? 500 : 600,
                        backgroundColor: isLight ? '#F1EDE6' : baseTheme.palette.action.selected,
                        color: isLight ? '#2C2926' : baseTheme.palette.text.primary,
                        border: isLight ? `1px solid ${baseTheme.palette.grey[200]}` : 'none',
                    },
                    colorSecondary: {
                        backgroundColor: isLight ? '#F1EDE6' : `${baseTheme.palette.secondary.main}20`,
                        color: isLight ? '#2C2926' : onSurface(baseTheme.palette.secondary),
                        border: isLight ? `1px solid ${baseTheme.palette.grey[200]}` : 'none',
                    },
                    colorPrimary: {
                        backgroundColor: `${baseTheme.palette.primary.main}${isLight ? '14' : '20'}`,
                        border: isLight ? `1px solid ${baseTheme.palette.primary.main}40` : 'none',
                        color: onSurface(baseTheme.palette.primary),
                    },
                    colorSuccess: {
                        backgroundColor: `${baseTheme.palette.success.main}${isLight ? '14' : '20'}`,
                        border: isLight ? `1px solid ${baseTheme.palette.success.main}40` : 'none',
                        color: onSurface(baseTheme.palette.success),
                    },
                    colorWarning: {
                        backgroundColor: `${baseTheme.palette.warning.main}${isLight ? '1F' : '20'}`,
                        border: isLight ? `1px solid ${baseTheme.palette.warning.main}4D` : 'none',
                        color: onSurface(baseTheme.palette.warning),
                    },
                    colorError: {
                        backgroundColor: `${baseTheme.palette.error.main}${isLight ? '14' : '20'}`,
                        border: isLight ? `1px solid ${baseTheme.palette.error.main}40` : 'none',
                        color: onSurface(baseTheme.palette.error),
                    },
                    colorInfo: {
                        backgroundColor: `${baseTheme.palette.info.main}${isLight ? '14' : '20'}`,
                        border: isLight ? `1px solid ${baseTheme.palette.info.main}40` : 'none',
                        color: onSurface(baseTheme.palette.info),
                    },
                },
                variants: [
                    // Intense solid accent — one standout pill per view, never for
                    // repeated table status tints.
                    {
                        props: { variant: 'solid' },
                        style: {
                            border: 'none',
                            backgroundColor: baseTheme.palette.primary.main,
                            color: baseTheme.palette.primary.contrastText,
                            fontWeight: 500,
                        },
                    },
                ],
            },

            MuiTextField: {
                defaultProps: {
                    variant: 'outlined',
                    size: 'small',
                },
                styleOverrides: {
                    root: {
                        '& .MuiInputBase-root': {
                            minHeight: '48px',
                        },
                    },
                },
            },

            MuiSelect: {
                styleOverrides: {
                    select: {
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: '22px',
                        paddingTop: '8.5px',
                        paddingBottom: '8.5px',
                    },
                    icon: {
                        top: 'calc(50% - 12px)',
                    },
                },
            },

            MuiInputBase: {
                styleOverrides: {
                    root: {
                        '&.MuiInputBase-sizeSmall': {
                            minHeight: '48px',
                        },
                        '&:not(.MuiInputBase-sizeSmall)': {
                            minHeight: '56px',
                        },
                    },
                    input: {
                        '&.MuiInputBase-inputSizeSmall': {
                            paddingTop: '8.5px',
                            paddingBottom: '8.5px',
                        },
                    },
                },
            },

            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        '&.MuiInputLabel-sizeSmall': {
                            transform: 'translate(14px, 9px) scale(1)',
                        },
                        '&.MuiInputLabel-shrink': {
                            transform: 'translate(14px, -9px) scale(0.75)',
                        },
                    },
                },
            },

            MuiFormControl: {
                styleOverrides: {
                    root: {
                        marginTop: 0,
                        marginBottom: 0,
                    },
                },
            },

            MuiAutocomplete: {
                styleOverrides: {
                    inputRoot: {
                        paddingTop: '4px',
                        paddingBottom: '4px',
                        '&.MuiInputBase-sizeSmall': {
                            paddingTop: '2px',
                            paddingBottom: '2px',
                            '& .MuiAutocomplete-input': {
                                paddingTop: '6.5px',
                                paddingBottom: '6.5px',
                            },
                        },
                    },
                },
            },

            MuiFormHelperText: {
                styleOverrides: {
                    root: {
                        marginTop: '3px',
                        marginLeft: '14px',
                        marginRight: '14px',
                    },
                },
            },
        },
    });
};

export default createAppTheme;
